/**
 * Alarm-severity colour vocabulary — single source of truth for how
 * severities rank and render, replacing the four hand-rolled per-file maps
 * (AlarmsPage, AlarmsCompact, SeverityPicker, RuleList).
 *
 * Backed by the dedicated --ems-alarm-* tokens (styles.css): critical red,
 * major orange, minor gold, warning steel — each with a dark-theme
 * override, so dark mode tracks for free. Emerald (--ems-ok) stays reserved
 * for healthy/acknowledged/live, the electric-blue brand for identity, and
 * discharge-orange for power direction — none is a severity colour.
 *
 * Class strings are complete static literals — Tailwind cannot see
 * runtime-composed names. `text-critical` etc. are the token-backed
 * --color-critical aliases; major/minor/warning have no named utility,
 * so they bind the token directly via the arbitrary-value form.
 */

import type { ChipTone } from "@/components/ui/Chip";

/** Sort rank — higher is more severe (TB convention: warning ranks lowest). */
export const SEV_ORDER = { critical: 4, major: 3, minor: 2, warning: 1 } as const;

const CHIP_TONE: Record<string, ChipTone> = {
  critical: "critical",
  major: "major",
  minor: "minor",
  warning: "warning",
};

const TEXT_CLASS: Record<string, string> = {
  critical: "text-critical",
  major: "text-[var(--ems-alarm-major)]",
  minor: "text-[var(--ems-alarm-minor)]",
  warning: "text-[var(--ems-alarm-warning)]",
};

const BADGE_CLASS: Record<string, string> = {
  critical: "border-critical/40 bg-critical/10 text-critical",
  major: "border-[var(--ems-alarm-major)]/40 bg-[var(--ems-alarm-major)]/10 text-[var(--ems-alarm-major)]",
  minor: "border-[var(--ems-alarm-minor)]/40 bg-[var(--ems-alarm-minor)]/10 text-[var(--ems-alarm-minor)]",
  warning: "border-[var(--ems-alarm-warning)]/40 bg-[var(--ems-alarm-warning)]/10 text-[var(--ems-alarm-warning)]",
};

/**
 * Chip tone per severity — Chip's critical/major/minor/warning tones already
 * point at the same tokens. Accepts either case ("major" / "MAJOR"); unknown
 * levels (e.g. INDETERMINATE) fall back to neutral.
 */
export function severityChipTone(level: string): ChipTone {
  return CHIP_TONE[level.toLowerCase()] ?? "neutral";
}

/** Text-colour-only class per severity. */
export function severityTextClass(level: string): string {
  return TEXT_CLASS[level.toLowerCase()] ?? "text-text-faint";
}

/** Border + soft fill + text — the icon-badge / severity-pill treatment. */
export function severityBadgeClass(level: string): string {
  return BADGE_CLASS[level.toLowerCase()] ?? "border-edge bg-surface-2/40 text-text-faint";
}
